import { paramsToString } from "./functions";

const WP_BASE_URL = "https://wp.hash.ai/wp-json/wp/v2";
export interface WordPressPost {
  id: number;
  acf?: any;
  content: {
    rendered: string;
  };
  date: string;
  excerpt: {
    rendered: string;
  };
  featured_image_url?: string;
  modified: string;
  slug: string;
  title: {
    rendered: string;
  };
  type: string;
}

export type WordPressResults = {
  page: number;
  pages: number;
  perPage: number;
  results: WordPressPost[];
  totalCount: number;
};

type WordPressRequestParams = {
  _fields?: string; // comma separated strings
  orderby?: string;
  order?: string;
  page?: number;
  per_page?: number;
  slug?: string;
};

type WordPressClient = {
  namespace: string;
  /**
   * Request multiple posts
   * @param params Optional parameters for filtering and pagination
   */
  get(params?: WordPressRequestParams, id?: number): Promise<WordPressResults>;
  resource(resource: string): WordPressClient;
  /**
   * Request a single post by slug
   * @param slug The post slug
   * @param params Additional WP API request params
   */
  slug(slug: string): Promise<WordPressPost | undefined>;
  /**
   * Request a single post by id
   * @param id The integer post id
   * @param params Additional WP API request params
   */
  id(id: number): Promise<WordPressPost | undefined>;
};

// Provide camelCase aliases for our WordPress post types
const WORDPRESS_RESOURCE_MAP = {
  cases: "case",
  comparisonFeatures: "comparison_feature",
  comparisonFeatureStatuses: "comparison_feature_status",
  competitors: "competitor",
  jobListingPosts: "job_listing",
  leadershipBioPosts: "leadership_bio",
  pages: "pages",
  posts: "posts",
  pressReleases: "press_release",
  productUpdatePosts: "product_update",
  wikis: "glossary",
};
type WordpressResourceAlias = keyof typeof WORDPRESS_RESOURCE_MAP;
// We'll provide functions to specify post type and chain requests
type ResourceHelpers = {
  [key in WordpressResourceAlias]: () => WordPressClient;
};

const headers = {
  "Content-Type": "application/json",
  Accept: "application/json",
};

const wpClient: WordPressClient & Partial<ResourceHelpers> = {
  namespace: "pages",

  async get(params?: WordPressRequestParams) {
    const requestParams = {
      per_page: 10,
      page: 1,
    };

    let url = `${WP_BASE_URL}/${this.namespace}`;
    if (params) {
      Object.assign(requestParams, params);
      url += `?${paramsToString(params)}`;
    }

    const response = await fetch(url, { headers });
    const results = (await response.json()) as WordPressPost[];
    const totalCount = parseInt(response.headers.get("X-WP-Total") ?? "0", 10);
    const pages = parseInt(response.headers.get("X-WP-TotalPages") ?? "0", 10);
    const page = requestParams.page;
    const perPage = requestParams.per_page;

    return {
      page,
      pages,
      perPage,
      results,
      totalCount,
    };
  },

  resource(resource: string) {
    this.namespace = resource;
    return this;
  },

  // Return a single post by slug
  async slug(slug: string) {
    return this.get({ per_page: 1, slug }).then(({ results }) => results[0]);
  },

  // Return a single post by id
  async id(id: number) {
    const url = `${WP_BASE_URL}/${this.namespace}/${id}`;
    return fetch(url, { headers })
      .then((resp) => resp.json())
      .then((json) => {
        if (json?.data?.status === 404) {
          return undefined;
        } else {
          return json as WordPressPost;
        }
      });
  },
};

// Add functions to specify particular namespaces using our aliases
for (const [alias, namespace] of Object.entries(WORDPRESS_RESOURCE_MAP)) {
  wpClient[alias as WordpressResourceAlias] = function () {
    return this.resource(namespace);
  };
}

export default wpClient as WordPressClient & ResourceHelpers;
